import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IGetPatientsThunkArgs,
	INewPatientDetails,
	ICreatePatientThunkArgs,
	IPatientDetails,
	IGetPatientDetailsThunkArgs,
	IUpdatePatientThunkArgs,
	IDeletePatientThunkArgs,
	IPatientBase,
} from 'src/features/patient/domain/types';
import { patientApi } from 'src/features/patient/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const PATIENT_LOADING_KEY = 'patient';

export const getPatients = createAsyncThunk<
	IPaginatedResponse<IPatientBase>,
	IGetPatientsThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'patient/getPatients',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: true }));
			const response = await patientApi.getPatients(request, siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to fetch patients');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: false }));
		}
	},
);

export const getPatientDetails = createAsyncThunk<
	IPatientDetails,
	IGetPatientDetailsThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'patient/getPatientDetails',
	async (
		{ patientId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: true }));
			const response = await patientApi.getPatientDetails(
				patientId,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch patient details',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: false }));
		}
	},
);

export const createPatient = createAsyncThunk<
	INewPatientDetails,
	ICreatePatientThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'patient/createPatient',
	async (
		{ siteSlug, onSuccess, onError, request },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: true }));
			const response = await patientApi.createPatient(siteSlug, request);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to create patient');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: false }));
		}
	},
);

export const updatePatient = createAsyncThunk<
	INewPatientDetails,
	IUpdatePatientThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'patient/updatePatient',
	async (
		{ siteSlug, onSuccess, onError, request, patientId },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: true }));
			const response = await patientApi.updatePatient(
				siteSlug,
				patientId,
				request,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to update patient');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: false }));
		}
	},
);

export const deletePatient = createAsyncThunk<
	void,
	IDeletePatientThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'patient/deletePatient',
	async (
		{ siteSlug, onSuccess, onError, patientId },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: true }));
			await patientApi.deletePatient(siteSlug, patientId);

			onSuccess?.();
			return;
		} catch (error: unknown) {
			const err = handleThunkError(error, 'Failed to delete patient');
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(setLoading({ key: PATIENT_LOADING_KEY, loading: false }));
		}
	},
);
